import {
  ArrowLeftStartOnRectangleIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  IdentificationIcon,
  QuestionMarkCircleIcon
} from "@heroicons/react/20/solid";
import { WalletIcon } from "@heroicons/react/24/solid";
import { usePrivy } from "@privy-io/react-auth";
import _ from "lodash";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Base, Info } from "@/atoms-react/text/Text.react";
import { AccountSelector } from "@/components/account-selector/index.react";
import config from "@/config";
import { useAuth } from "@/features/auth/hooks/use-auth-react";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { KYCStatusType } from "@/features/kyc/types";
import { Routes } from "@/features/router";
import { useUser } from "@/features/user/hooks/use-user";
import type { identityPartners } from "@/features/user/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { MenuItem } from "./index.react";
import { useCheckCompanyWaitingPayment } from "@/features/company/hooks/use-company-waiting-payment";

type MenuProps = {
  open: boolean;
  smallVersion?: boolean;
  isAdmin: boolean;
  onOlkyPassClick?: () => void;
  onOlkyWalletClick?: () => void;
  updatePartnerChange: (
    partner: FromURL,
    url: string,
    partnerType?: string,
    lang?: string
  ) => Promise<void>;
} & React.ComponentPropsWithoutRef<"div">;
export const Menu = ({
  open,
  updatePartnerChange,
  smallVersion = false,
  isAdmin = false,
  onOlkyPassClick = () => {},
  ...props
}: MenuProps) => {
  const { t } = useTranslation(["pass"]);
  const { logout } = useAuth();
  const { status } = useKYCContext();
  const { useUserProfile } = useUser();
  const { data: profile } = useUserProfile();
  const origin = useOrigin();
  const navigate = useNavigate();
  const { getLanguage } = usei18n();
  const { leUuid } = useCompanyContext();
  const { logout: privyLogout, authenticated } = usePrivy();
  const { isWaitingPayment } = useCheckCompanyWaitingPayment();

  return (
    <div
      className={twMerge(
        "absolute z-0 flex h-full shrink-0 flex-col overflow-hidden overflow-y-auto border-slate-50 border-r bg-white py-6 2xl:relative dark:border-slate-900 dark:bg-slate-950",
        "-translate-x-full transition-width 2xl:translate-x-0",
        "z-20 px-4 2xl:px-8",
        open ? "translate-x-0" : "",
        smallVersion ? "w-16 px-auto 2xl:w-24" : "w-64 2xl:w-80"
      )}
      {..._.omit(props, "className")}
    >
      <AccountSelector smallVersion={smallVersion} isAdmin={isAdmin} />
      {!isAdmin ? (
        <>
          {leUuid && (
            <div className="mt-5">
              <div
                onClick={async () => {
                  navigate(`${Routes.companies}`);
                  if (authenticated) await privyLogout();
                }}
                className="flex cursor-pointer items-center"
              >
                <ChevronLeftIcon
                  className={twMerge("mr-2 h-6 w-6 dark:text-white", smallVersion ? "hidden" : "")}
                />
                <Base className={twMerge("font-semibold", smallVersion ? "hidden" : "")}>
                  {t("olkypass.mainPage.navigation_menu.link.my_companies")}
                </Base>
              </div>
            </div>
          )}
          <Info className="mt-10 block"></Info>
          <MenuItem
            disabled={isWaitingPayment}
            to={origin === FromURL.None ? Routes.dashboard : ""}
            onClick={async () => {
              if (origin === FromURL.None)
                return leUuid
                  ? navigate(`${Routes.dashboard}/${leUuid}`)
                  : navigate(Routes.dashboard);
              await updatePartnerChange(FromURL.None, config.OLKY_PASS_URL, "", getLanguage());
            }}
            smallVersion={smallVersion}
            icon={(p) => <ChartBarIcon {...p} />}
          >
            {t("olkypass.mainPage.navigation_menu.link.home")}
          </MenuItem>
          {leUuid !== null ? (
            <>
              <Info className={twMerge("mt-5 block")}>
                <span
                  className={twMerge(
                    "inline-block transition-transform",
                    smallVersion && "translate-x-[calc(-50%+1rem)]"
                  )}
                >
                  {t("olkypass.mainPage.navigation_menu.link.company_data")}
                </span>
              </Info>
              <MenuItem
                disabled={isWaitingPayment}
                smallVersion={smallVersion}
                icon={(p) => <IdentificationIcon {...p} />}
                to={`${Routes.level}/${leUuid}`}
                dataCy="navigation-menu-item-myolkypass"
              >
                <div
                  data-cy="navigation-menu-item-myolkypasspro"
                  className="flex cursor-pointer items-center justify-between"
                >
                  <span className="block">
                    {t("olkypass.mainPage.navigation_menu.link.my_pass_pro")}
                    {status === KYCStatusType.DECLARATIVE && (
                      <ExclamationCircleIcon
                        data-position={"right"}
                        data-tooltip={t("olkypass.level.declarative_label")}
                        className="ml-2 inline h-5 w-auto text-red-500"
                      />
                    )}
                  </span>
                  <ChevronRightIcon
                    className={twMerge("-mr-2 h-6 w-6", smallVersion ? "hidden" : "")}
                  />
                </div>
              </MenuItem>
              <MenuItem
                disabled={isWaitingPayment}
                smallVersion={smallVersion}
                icon={(p) => <BuildingLibraryIcon {...p} />}
                to={`${Routes.companies}/${leUuid}/related_person`}
              >
                <div data-cy="navigation-menu-item-company-representative">
                  {t("olkypass.mainPage.navigation_menu.link.company_representative")}
                </div>
              </MenuItem>
            </>
          ) : (
            <>
              <Info className={twMerge("mt-5 block")}>
                <span
                  className={twMerge(
                    "inline-block transition-transform",
                    smallVersion && "translate-x-[calc(-50%+1rem)]"
                  )}
                >
                  {t("olkypass.mainPage.navigation_menu.link.my_account")}
                </span>
              </Info>
              <MenuItem
                to={Routes.level}
                data-cy="navigation-menu-item-myolkypass"
                smallVersion={smallVersion}
                icon={(p) => <IdentificationIcon {...p} />}
                dataCy="navigation-menu-item-myolkypass"
              >
                <div className="flex cursor-pointer items-center justify-between">
                  <span className="block">
                    {t("olkypass.mainPage.navigation_menu.link.my_pass")}
                    {status === KYCStatusType.DECLARATIVE && (
                      <ExclamationCircleIcon
                        data-position={"right"}
                        data-tooltip={t("olkypass.level.declarative_label")}
                        className="ml-2 inline h-5 w-auto text-red-500"
                      />
                    )}
                  </span>
                  <ChevronRightIcon
                    className={twMerge("-mr-2 h-6 w-6", smallVersion ? "hidden" : "")}
                  />
                </div>
              </MenuItem>
              {config.ACTIVATE_OLKYPASS_PRO && (
                <MenuItem
                  to={Routes.companies}
                  smallVersion={smallVersion}
                  icon={(p) => <BuildingLibraryIcon {...p} />}
                  dataCy="navigation-menu-item-mycompanies"
                >
                  {t("company.label.main_title")}
                </MenuItem>
              )}
            </>
          )}
          {profile!.identityPartners && profile!.identityPartners?.length > 0 && (
            <Info className={twMerge("mt-5 block")}>
              <span
                className={twMerge(
                  "inline-block transition-transform",
                  smallVersion && "translate-x-[calc(-50%+1rem)]"
                )}
              >
                {t("olkypass.mainPage.navigation_account_menu.link.my_partners")}
              </span>
            </Info>
          )}
          {profile?.identityPartners.map((partner: identityPartners) => {
            return (
              <div key={partner.uuid}>
                <MenuItem
                  disabled={isWaitingPayment}
                  to={
                    leUuid
                      ? `${Routes.dashboard}/${leUuid}/${partner?.name?.toLocaleLowerCase()}`
                      : `${Routes.dashboard}/${partner?.name?.toLocaleLowerCase()}`
                  }
                  onClick={async () => {
                    if (
                      origin === FromURL.Partner &&
                      sessionStorage.getItem("origin") === partner?.name?.toLocaleLowerCase()
                    )
                      return navigate(
                        leUuid
                          ? `${Routes.dashboard}/${leUuid}/${partner?.name?.toLocaleLowerCase()}`
                          : `${Routes.dashboard}/${partner?.name?.toLocaleLowerCase()}`
                      );
                    await updatePartnerChange(
                      FromURL.Partner,
                      partner.host,
                      partner?.name,
                      getLanguage()
                    );
                  }}
                  smallVersion={smallVersion}
                  icon={(p) => <WalletIcon {...p} />}
                >
                  {partner?.name?.charAt(0)?.toUpperCase() + partner?.name?.slice(1)}
                </MenuItem>
              </div>
            );
          })}
          <div className="flex-grow"></div>
          <Info className={twMerge("mt-5 block")}>
            <span
              className={twMerge(
                "inline-block transition-transform",
                smallVersion && "translate-x-[calc(-50%+1rem)]"
              )}
            >
              {t("olkypass.mainPage.navigation_menu.link.account")}
            </span>
          </Info>
          <MenuItem
            to={Routes.support}
            smallVersion={smallVersion}
            icon={(p) => <QuestionMarkCircleIcon {...p} />}
          >
            {t("olkypass.mainPage.navigation_account_menu.link.support")}
          </MenuItem>
          <MenuItem
            dataCy="navigation-menu-item-security"
            to={Routes.security}
            smallVersion={smallVersion}
            icon={(p) => <CheckBadgeIcon {...p} />}
          >
            {t("olkypass.mainPage.navigation_account_menu.link.security")}
          </MenuItem>
          <MenuItem
            dataCy="navigation-menu-item-logout"
            onClick={async () => {
              if (authenticated) await privyLogout();
              await logout();
            }}
            smallVersion={smallVersion}
            icon={(p) => <ArrowLeftStartOnRectangleIcon {...p} />}
          >
            {t("olkypass.mainPage.navigation_menu.link.logout")}
          </MenuItem>
        </>
      ) : (
        <div className="mt-8">
          <MenuItem
            to={`${Routes.pass}/new`}
            smallVersion={smallVersion}
            icon={(p) => <IdentificationIcon {...p} />}
            dataCy="navigation-menu-item-admin-new"
          >
            {t("admin_page.menu.new")}
          </MenuItem>
          <MenuItem
            to={Routes.dashboard}
            smallVersion={smallVersion}
            icon={(p) => <IdentificationIcon {...p} />}
          >
            OlkyPass Pro
          </MenuItem>
          <MenuItem
            dataCy="navigation-menu-item-logout"
            onClick={async () => {
              if (authenticated) await privyLogout();
              await logout();
            }}
            smallVersion={smallVersion}
            icon={(p) => <ArrowLeftStartOnRectangleIcon {...p} />}
          >
            {t("olkypass.mainPage.navigation_menu.link.logout")}
          </MenuItem>
        </div>
      )}
    </div>
  );
};
